// src/components/admin/RecordModal.js
import React, { useState } from 'react';

const RecordModal = ({ record, onClose, onDelete }) => {
  const [activeTab, setActiveTab] = useState('general');
  
  // Format and render complex nested objects and arrays
  const formatValue = (value, depth = 0) => {
    if (value === null || value === undefined) {
      return <span className="italic text-gray-500 dark:text-gray-400">null</span>;
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-500 dark:text-gray-400">[ ]</span>;
      }
      
      return (
        <div className="pl-4 border-l-2 border-gray-200 dark:border-gray-700">
          {value.map((item, index) => (
            <div key={index} className="mb-1">
              <span className="font-medium text-blue-600 dark:text-blue-400 mr-1">[{index}]:</span>
              {formatValue(item, depth + 1)}
            </div>
          ))}
        </div>
      );
    }
    
    if (typeof value === 'object') {
      return (
        <div className="pl-4" style={{ marginLeft: `${depth * 8}px` }}>
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="mb-1">
              <span className="font-medium text-purple-600 dark:text-purple-400 mr-1">{key}:</span>
              {formatValue(val, depth + 1)}
            </div>
          ))}
        </div>
      );
    }
    
    if (typeof value === 'boolean') {
      return <span className="text-green-600 dark:text-green-400">{value.toString()}</span>;
    }
    
    if (typeof value === 'number') {
      return <span className="text-orange-600 dark:text-orange-400">{value}</span>;
    }
    
    return <span className="text-gray-800 dark:text-gray-200">"{value.toString()}"</span>;
  };
  
  // Filter record to show only relevant fields based on active tab
  const getFieldsForTab = () => {
    if (!record) return [];
    
    const generalFields = ['id', 'logicalId', 'remark', 'designationDetails', 'entityType', 'subjectType', 'dataSources', 'importDate'];
    const nameFields = ['names'];
    const identityFields = ['birthdates', 'citizenships', 'function', 'gender', 'title'];
    const regulationFields = ['regulation', 'euReferenceNumber', 'unitedNationId'];
    
    switch (activeTab) {
      case 'general':
        return Object.entries(record).filter(([key]) => generalFields.includes(key));
      case 'names':
        return Object.entries(record).filter(([key]) => nameFields.includes(key));
      case 'identity':
        return Object.entries(record).filter(([key]) => identityFields.includes(key));
      case 'regulation':
        return Object.entries(record).filter(([key]) => regulationFields.includes(key));
      case 'all':
        return Object.entries(record);
      default:
        return [];
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white">Record Details</h2>
          <button 
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none transition-colors"
            onClick={onClose}
          >
            &times;
          </button>
        </div>
        
        <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          <button 
            className={`px-4 py-2 text-sm font-medium border-b-2 focus:outline-none ${
              activeTab === 'general' 
              ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
              : 'border-transparent text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
          <button 
            className={`px-4 py-2 text-sm font-medium border-b-2 focus:outline-none ${
              activeTab === 'names' 
              ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
              : 'border-transparent text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
            onClick={() => setActiveTab('names')}
          >
            Names
          </button>
          <button 
            className={`px-4 py-2 text-sm font-medium border-b-2 focus:outline-none ${
              activeTab === 'identity' 
              ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
              : 'border-transparent text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
            onClick={() => setActiveTab('identity')}
          >
            Identity
          </button>
          <button 
            className={`px-4 py-2 text-sm font-medium border-b-2 focus:outline-none ${
              activeTab === 'regulation' 
              ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
              : 'border-transparent text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
            onClick={() => setActiveTab('regulation')}
          >
            Regulation
          </button>
          <button 
            className={`px-4 py-2 text-sm font-medium border-b-2 focus:outline-none ${
              activeTab === 'all' 
              ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
              : 'border-transparent text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
            onClick={() => setActiveTab('all')}
          >
            All Fields
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {record ? (
            <div className="space-y-6">
              {getFieldsForTab().map(([key, value]) => (
                <div key={key} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0">
                  <h3 className="text-sm uppercase font-semibold text-gray-500 dark:text-gray-400 mb-2">{key}</h3>
                  <div className="text-sm text-gray-800 dark:text-gray-200 font-mono">
                    {formatValue(value)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">Loading record details...</p>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
          <button 
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
            onClick={onDelete}
          >
            Delete Record
          </button>
          <button 
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded transition-colors"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordModal;